// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useRevokeCredential from '@hooks/mutations/credential/useRevokeCredential';
import useRevokeCredentialAndCancelBilling from '@hooks/mutations/credential/useRevokeCredentialAndCancelBilling';
import { useCredentialBillings } from '@hooks/queries/credential/billing/useCredentialBillings';
import { useCredentialUserService } from '@hooks/queries/credential/onboarding/useUserService';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { ButtonProps } from '@mui/material';
import { Credential } from '@daml.js/utility-credential-v0/lib/Utility/Credential/V0/Credential';
import { useParties } from '../../contexts/PartiesContext';
import { useScanProxyApiClient } from '../../contexts/ScanProxyApiContext';
import TemplateContract from '../../utils/TemplateContract';
import { appTransferContextWithDisclosures } from '../../utils/appTransferContextWithDisclosures';
import { ERR_REV_CRED } from '../../utils/strings';

export const useCreateCredentialActions = (
  onClose: () => void,
  r?: TemplateContract<Credential>,
) => {
  const { party } = useParties();
  const billings = useCredentialBillings();
  const userService = useCredentialUserService();
  const { showBoundary } = useErrorBoundary<Error>();
  const { enqueueSnackbar } = useSnackbar();

  const scanProxyApi = useScanProxyApiClient();

  const revokeCredential = useRevokeCredential();
  const revokeAndCancelBilling = useRevokeCredentialAndCancelBilling();

  const findCredentialBillingById = useCallback(
    (credentialId: string) => {
      return billings.data?.find((b) => b.payload.credentialId === credentialId);
    },
    [billings.data],
  );

  const revoke = async () => {
    if (!userService.data || !r) {
      throw new Error('No user service');
    }
    await revokeCredential({
      cid: userService.data.contractId,
      argument: {
        credentialCid: r.contractId,
      },
    });
    onClose();
  };

  const revokeAndCancel = useCallback(async () => {
    if (!userService.data || !r) {
      showBoundary(new Error(ERR_REV_CRED, { cause: 'User service is not available.' }));
      throw new Error(ERR_REV_CRED);
    }
    const billing = findCredentialBillingById(r.payload.id);
    if (!billing) {
      enqueueSnackbar(`${ERR_REV_CRED}: 'Billing could not be found.`, {
        variant: 'error',
      });
      throw new Error(ERR_REV_CRED);
    }

    const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
      scanProxyApi,
      30,
    );

    await revokeAndCancelBilling({
      cid: userService.data.contractId,
      argument: {
        appTransferContext,
        credentialCid: r.contractId,
        credentialBillingCid: billing.contractId,
      },
      meta: {
        disclosedContracts,
      },
    });
    onClose();
  }, [
    userService.data,
    findCredentialBillingById,
    revokeAndCancelBilling,
    scanProxyApi,
    enqueueSnackbar,
    showBoundary,
    onClose,
    r,
  ]);

  if (!r) {
    return [];
  }

  const billing = findCredentialBillingById(r.payload.id);
  if (billing) {
    return [
      { color: 'error' as ButtonProps['color'], name: 'Revoke', action: revoke },
      {
        color: 'error' as ButtonProps['color'],
        name: 'Revoke And Cancel',
        action: revokeAndCancel,
      },
    ];
  }
  const isIssuer = r.payload.issuer === party;
  const isHolder = r.payload.holder === party;
  if (isIssuer || isHolder) {
    return [{ color: 'error' as ButtonProps['color'], name: 'Revoke', action: revoke }];
  }
  return [];
};
